/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useMemo, useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/mousewheel";
import "swiper/css/navigation";
import { A11y, Keyboard, Mousewheel, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Product from "web/Layout/ProductCard";
import __ from "web/Layout/Translations";

import useAccessibleSwiperCarousel from "web/hooks/useAccessibleSwiperCarousel";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import type { PropsWithClasses } from "web/types/Common";
import type { ProductInList } from "web/types/Product";

import classify from "web/classify";

import defaultClasses from "./productCarousel.scss";

const LOOP_BUFFER_FACTOR = 3;

interface IProductCarouselProps {
  products: ProductInList[];
  listingPosition?: number;
  listingAmount?: number;
  listingCategory?: string;
}

const ProductCarousel: FC<PropsWithClasses<IProductCarouselProps>> = ({
  products = [],
  classes = {},
  listingPosition = null,
  listingAmount = null,
  listingCategory = "",
}) => {
  const statusId = `carousel-status-${listingPosition}-${listingAmount}-${listingCategory}`;

  const totalSlides = products.length;

  const [visibleCount, setVisibleCount] = useState(0);

  const isOverflowing = visibleCount > 0 && totalSlides > visibleCount;

  const copies =
    isOverflowing && totalSlides > 0
      ? Math.max(
          2,
          Math.ceil((visibleCount * LOOP_BUFFER_FACTOR) / totalSlides)
        )
      : 1;

  const displayProducts = useMemo(
    () =>
      copies > 1
        ? Array.from({ length: copies }, (_, copyIndex) =>
            products.map((product, productIndex) => ({
              product,
              key: `${product.id}-${copyIndex}`,
              positionIndex: productIndex + 1,
            }))
          ).flat()
        : products.map((product, productIndex) => ({
            product,
            key: `${product.id}`,
            positionIndex: productIndex + 1,
          })),
    [products, copies]
  );

  const { swiperRef, swiperA11yProps, containerProps } =
    useAccessibleSwiperCarousel({ deps: [displayProducts] });

  const updateOverflow = (swiper: SwiperClass) => {
    const slide = swiper.slides?.[0];
    if (!slide) {
      return;
    }
    const style = getComputedStyle(slide);
    const slideFullWidth =
      slide.offsetWidth +
      parseFloat(style.marginLeft || "0") +
      parseFloat(style.marginRight || "0");
    if (slideFullWidth > 0) {
      setVisibleCount(Math.floor(swiper.width / slideFullWidth));
    }
  };

  useEffect(() => {
    const container = containerProps.ref?.current;
    if (container) {
      const prev = container.querySelector(".swiper-button-prev");
      const next = container.querySelector(".swiper-button-next");

      const swiperContainer = container.querySelector(".swiper");

      if (swiperContainer) {
        (swiperContainer as HTMLElement).style.position = "static";
      }

      if (prev && next) {
        prev.setAttribute("tabindex", "0");
        next.setAttribute("tabindex", "0");
        prev.setAttribute("aria-disabled", "false");
        next.setAttribute("aria-disabled", "false");
      }
    }
  }, [containerProps.ref]);

  return isArrayHasItems(products) ? (
    <div className={classes.carouselContainer} {...containerProps}>
      <div id={statusId} aria-live="polite" className="sr-only" />
      <Swiper
        modules={[Mousewheel, Navigation, A11y, Keyboard]}
        slidesPerView="auto"
        mousewheel={{ forceToAxis: true, sensitivity: 2 }}
        navigation
        watchOverflow
        centerInsufficientSlides
        loop={isOverflowing}
        aria-label={__("Karuzela produktów")}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          swiper.keyboard.disable();
          updateOverflow(swiper);
        }}
        onResize={updateOverflow}
        onSlideChange={(swiper) => {
          const active = (swiper.realIndex % totalSlides) + 1;
          const live = document.getElementById(statusId);

          if (live) {
            live.textContent = `${__("Slajd")} ${active} ${__(
              "z"
            )} ${totalSlides}`;
          }
        }}
        wrapperTag="ul"
        {...swiperA11yProps}
      >
        {displayProducts.map(({ product, key, positionIndex }) => {
          return (
            <SwiperSlide
              key={key}
              role="group"
              aria-roledescription="slide"
              aria-label={`Slajd ${positionIndex} z ${totalSlides}`}
              tag="li"
            >
              <Product
                product={product}
                positionNumber={positionIndex}
                listingPosition={listingPosition as number}
                listingAmount={listingAmount as number}
                listingCategory={listingCategory}
                variant="promo"
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  ) : null;
};

export default classify<PropsWithClasses<IProductCarouselProps>>(
  defaultClasses
)(ProductCarousel);
